// Backdrop of the signed-out and account screens, shared with the admin console and the driver app
// (constitution 1.1.0): the scene colour lit from the top-left and behind the card, wave lines, the
// Akaiva book mark rising from the bottom-left and, in the dark theme, stars and a brighter book.
// Decorative.
import { Image } from "expo-image";
import { Platform, StyleSheet, View } from "react-native";
import Svg, {
  Circle,
  Defs,
  Path,
  RadialGradient,
  Rect,
  Stop,
} from "react-native-svg";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";

import MARK from "../../../assets/akaiva-mark.png";

/** Mark artwork is 228 × 180. */
const MARK_ASPECT = 228 / 180;
const MARK_WIDTH = 480;

/** Faint stars across the dark backdrop (x, y in a 1600 × 1000 field; radius). */
const STARS: readonly [number, number, number][] = [
  [1520, 70, 2.2],
  [1380, 210, 1.4],
  [1240, 120, 1],
  [1460, 420, 1.6],
  [1100, 60, 1.2],
  [980, 300, 1],
  [1560, 640, 1.2],
  [760, 80, 1],
  [620, 240, 1.4],
  [300, 90, 1],
];

const hidden =
  Platform.OS === "web"
    ? { "aria-hidden": true }
    : {
        accessible: false,
        importantForAccessibility: "no-hide-descendants" as const,
      };

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { ...StyleSheet.absoluteFill, overflow: "hidden" },
    fill: { ...StyleSheet.absoluteFill },
    mark: {
      position: "absolute",
      bottom: -t.space.x3l,
      left: -t.space.x2l,
      width: MARK_WIDTH,
      maxWidth: "45%",
      aspectRatio: MARK_ASPECT,
      opacity: t.name === "dark" ? 0.85 : 0.3,
    },
  }),
);

export function AuthBackdrop() {
  const styles = useStyles();
  const theme = useTheme();
  const dark = theme.name === "dark";
  return (
    <View
      style={styles.root}
      pointerEvents="none"
      testID="auth-backdrop"
      {...hidden}
    >
      <Svg style={styles.fill} width="100%" height="100%">
        <Defs>
          <RadialGradient id="auth-lit" cx="12%" cy="0%" rx="60%" ry="55%">
            <Stop offset="0" stopColor={theme.color.sceneHi} />
            <Stop offset="1" stopColor={theme.color.scene} stopOpacity={0} />
          </RadialGradient>
          <RadialGradient id="auth-glow" cx="85%" cy="45%" rx="45%" ry="45%">
            <Stop
              offset="0"
              stopColor={theme.color.sceneHi}
              stopOpacity={0.45}
            />
            <Stop offset="1" stopColor={theme.color.scene} stopOpacity={0} />
          </RadialGradient>
        </Defs>
        <Rect width="100%" height="100%" fill="url(#auth-lit)" />
        <Rect width="100%" height="100%" fill="url(#auth-glow)" />
      </Svg>
      {dark ? (
        <Svg
          style={styles.fill}
          width="100%"
          height="100%"
          viewBox="0 0 1600 1000"
          preserveAspectRatio="xMidYMid slice"
          fill={theme.color.sceneStar}
          opacity={0.8}
        >
          {STARS.map(([x, y, r]) => (
            <Circle key={`${x}-${y}`} cx={x} cy={y} r={r} />
          ))}
        </Svg>
      ) : null}
      <Svg
        style={styles.fill}
        width="100%"
        height="100%"
        viewBox="0 0 1600 1000"
        preserveAspectRatio="none"
        fill="none"
        stroke={theme.color.sceneLine}
        strokeWidth={1.5}
        opacity={dark ? 0.8 : 0.55}
      >
        <Path
          d="M0 590 C 260 640, 420 760, 640 830 S 1000 900, 1180 860"
          vectorEffect="non-scaling-stroke"
        />
        <Path
          d="M0 615 C 250 670, 400 790, 620 860 S 980 930, 1150 900"
          vectorEffect="non-scaling-stroke"
        />
        <Path
          d="M1080 1000 C 1200 820, 1340 660, 1600 590"
          vectorEffect="non-scaling-stroke"
        />
        <Path
          d="M1120 1000 C 1240 840, 1380 690, 1600 615"
          vectorEffect="non-scaling-stroke"
        />
        <Path
          d="M1260 0 C 1480 160, 1600 380, 1600 560"
          vectorEffect="non-scaling-stroke"
        />
      </Svg>
      <Image
        source={MARK}
        style={styles.mark}
        contentFit="contain"
        blurRadius={1}
      />
    </View>
  );
}
